import { ChangeDetectionStrategy, Component, NgZone } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { ActiveAuthenticationStateService } from '@auth-n/state/active-authentication.state-service';
import { environment } from '@env/environment';
import { filter, map, startWith, take, tap } from 'rxjs/operators';
import { AuthenticationService } from '@auth-n/authentication.service';

declare var window: any;

/**
 * Nothing more than a router outlet for the {@link: AppRoutingModule}.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AppComponent {
  activeIdentity$ = this.activeAuthentication.activeIdentity$;

  // do not load app-new-layout when route is under-maintenance
  isMaintenanceRoute$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event: NavigationEnd) => event.url.includes('/under-maintenance')),
    startWith(window.location.pathname.includes('/under-maintenance'))
  );

  // do not load app-new-layout when route is an error page
  isErrorPageRoute$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event: NavigationEnd) => {
      const errorRoutes = ['/no-permissions', '/forbidden', '/locked'];
      return errorRoutes.some((route) => event.url.includes(route));
    }),
    startWith(false)
  );

  // render the default outlet without requiring activeIdentity for /logout
  isLogoutRoute$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event: NavigationEnd) => event.url === '/logout'),
    startWith(window.location.pathname === '/logout')
  );

  constructor(
    private router: Router,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private ngZone: NgZone,
    private activeAuthentication: ActiveAuthenticationStateService,
    private authenticationService: AuthenticationService
  ) {
    this.matIconRegistry.addSvgIcon(
      'pound-sign',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../framework/design-system/assets/images/currency-gbp.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'file-pack',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../framework/design-system/assets/images/pack-icon.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'download-bank-file',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../framework/design-system/assets/images/download-bank-file.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'download-bank-file-batch',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../framework/design-system/assets/images/download-bank-file-batch.svg')
    );

    if (environment.testSupportEnabled) this.createTestSupport(this.ngZone, this.router);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        take(1)
      )
      .subscribe((event: NavigationStart) => {
        this.authenticationService.setInitialUrl(event.url);
      });

    this.reattachMaintenanceRouteAfterLayoutSwitch();
  }

  /**
   * When navigating to /under-maintenance while authenticated, the template switches
   * from <app-new-layout> (which owns the primary <router-outlet>) to a bare
   * <router-outlet>. This destroys the outlet the router already attached the component to.
   * Re-navigate once on the next tick so the new outlet picks up the route.
   */
  private reattachMaintenanceRouteAfterLayoutSwitch(): void {
    let hasReattached = false;
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter((event) => event.url.includes('/under-maintenance'))
      )
      .subscribe(() => {
        if (this.activeAuthentication.activeIdentity && !hasReattached) {
          hasReattached = true;
          setTimeout(() => this.router.navigateByUrl('/under-maintenance').then());
        }
      });
  }

  private createTestSupport(ngZone: NgZone, router: Router) {
    // see also: e2e/cypress/support/ng-route-to.ts
    window.ngNavigateByUrl = function (url: string) {
      return new Promise<boolean>((resolve, reject) => {
        ngZone.run(() => {
          router.navigateByUrl(url).then(resolve, reject);
        });
      });
    };

    Object.defineProperty(document, 'referrer', {
      get() {
        return 'https://hr.test.cintra.cloud';
      }
    });
  }
}
